import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { SubscriptionGate } from '@/components/paywall/SubscriptionGate';
import { learnerApiJson, getInstallationId } from '@/lib/learner-api';
import { track } from '@/lib/analytics';
import { crosswordPickerGateCopy } from '@/lib/paywall';
import { useLearnerAuth } from '@/providers/LearnerAuth';
import { usePremiumAccess } from '@/hooks/usePremiumAccess';
import { typeScale } from '@/lib/typography';

type Puzzle = { puzzle_date: string; title: string; completed: boolean; personal_best_seconds: number | null };

export const Route = createFileRoute('/app/crosswords/')({ component: CrosswordsPage });

function CrosswordsPage() {
  const auth = useLearnerAuth();
  const premium = usePremiumAccess();
  const navigate = useNavigate();
  const puzzles = useQuery({
    queryKey: ['crosswords'],
    queryFn: () => learnerApiJson<Puzzle[]>(auth?.getToken ?? null, `/v1/crosswords?installation_id=${getInstallationId()}`),
    enabled: premium.isSubscribed,
  });
  useEffect(() => {
    track('Crossword Picker Screen');
  }, []);
  if (premium.isReady && !premium.isSubscribed) {
    const gate = crosswordPickerGateCopy();
    return (
      <SubscriptionGate
        source={gate.source}
        content="crosswords"
        message={gate.message}
        heroEmoji={gate.heroEmoji}
        onDismiss={() => void navigate({ to: '/app/games' })}
      />
    );
  }
  return (
    <div className="mx-auto max-w-4xl px-5 py-8 sm:px-8 lg:py-12">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className={typeScale.screenTitle}>Crosswords</h1>
          <p className="mt-3 text-slate-600">Practice vocabulary with the daily puzzle and archive.</p>
        </div>
        <Link to="/app/crosswords/leaderboard" className="font-bold text-[#2d7a13]">
          Leaderboard
        </Link>
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {puzzles.data?.map((puzzle) => (
          <Link
            key={puzzle.puzzle_date}
            to="/app/crosswords/$date"
            params={{ date: puzzle.puzzle_date }}
            className="rounded-3xl border border-[#cfe8c6] bg-white p-6 hover:border-[#399918]"
          >
            <span className="text-sm font-bold text-[#399918]">
              {new Date(`${puzzle.puzzle_date}T00:00:00`).toLocaleDateString()}
            </span>
            <h2 className="mt-2 font-nunito text-[22px] font-extrabold leading-[26px]">{puzzle.title}</h2>
            <p className="mt-3 text-sm text-slate-500">
              {puzzle.completed
                ? `Completed${puzzle.personal_best_seconds ? ` · Best ${Math.round(puzzle.personal_best_seconds)}s` : ''}`
                : 'Not completed'}
            </p>
          </Link>
        ))}
      </div>
      {puzzles.isLoading && <p className="mt-8">Loading puzzles…</p>}
    </div>
  );
}
